import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi } from '../services/api';
import api from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]                   = useState(null);
  const [loading, setLoading]             = useState(true);
  const [activeTenantId, setActiveTenantId] = useState(null);
  const [activeTenantName, setActiveTenantName] = useState(null);
  const [tenants, setTenants]             = useState([]);

  // Inject X-Tenant-ID header on every request when admin is viewing a different tenant
  useEffect(() => {
    const interceptor = api.interceptors.request.use(config => {
      if (activeTenantId) {
        config.headers['X-Tenant-ID'] = activeTenantId;
      } else {
        delete config.headers['X-Tenant-ID'];
      }
      return config;
    });
    return () => api.interceptors.request.eject(interceptor);
  }, [activeTenantId]);

  const loadTenants = useCallback(async () => {
    try {
      const { data } = await authApi.listTenants();
      setTenants(data);
    } catch { /* non-admin — ignore */ }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      authApi.me()
        .then(r => {
          setUser(r.data);
          if (r.data.is_admin) loadTenants();
        })
        .catch(() => localStorage.removeItem('token'))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [loadTenants]);

  const login = async (email, password) => {
    const { data } = await authApi.login(email, password);
    localStorage.setItem('token', data.access_token);
    setUser(data.user);
    if (data.user.is_admin) {
      // Load tenants for switcher
      try {
        const { data: ts } = await authApi.listTenants();
        setTenants(ts);
      } catch { /* ignore */ }
    }
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setActiveTenantId(null);
    setActiveTenantName(null);
    setTenants([]);
  };

  const switchTenant = (id, name) => {
    setActiveTenantId(id || null);
    setActiveTenantName(name || null);
  };

  const clearTenant = () => {
    setActiveTenantId(null);
    setActiveTenantName(null);
  };

  return (
    <AuthContext.Provider value={{
      user, setUser, login, logout, loading,
      tenants, activeTenantId, activeTenantName,
      switchTenant, clearTenant,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
